import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders, hasAdminCredentials } from '../../utils/adminAuth';
import { deletePlayerUrl, entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import { PlayerStatsInterface } from '../../models/PlayerStats';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const RemoveHeader = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #333;
  border-radius: 10px;
  background: #020617;
`;

const RowsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RowLabel = styled.span`
  font-size: 0.9rem;
  color: var(--gray);
`;

type Step = 'select' | 'confirm' | 'success' | 'error';

type DeletePlayerResponse = {
  success: boolean;
  summary: {
    requested: number;
    deleted: number;
    skipped: number;
    notFound: number;
    errors: number;
  };
  details: {
    deleted: string[];
    skipped: string[];
    notFound: string[];
    errors: string[];
  };
};

const MAX_ROWS = 22;

const DeletePlayer: React.FC = () => {
  const [eligiblePlayers, setEligiblePlayers] = useState<string[]>([]);
  const [loadingEligible, setLoadingEligible] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Each row is a separate dropdown selection
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>(['']);
  const [step, setStep] = useState<Step>('select');

  const [pendingDelete, setPendingDelete] = useState<string[]>([]);
  const [deleteResult, setDeleteResult] = useState<DeletePlayerResponse | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  const session = getAdminSession();

  useEffect(() => {
    const loadEligiblePlayers = async () => {
      const activeSession = getAdminSession();
      if (!hasAdminCredentials(activeSession)) return;

      try {
        setLoadingEligible(true);
        setError(null);

        const CACHE_TTL_MS = 5 * 60 * 1000;

        const [skaterStatsItems, goalieStatsItems] = await Promise.all([
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_stats'),
            CACHE_KEYS.stats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
        ]);

        const zeroGpNames = [
          ...(skaterStatsItems ?? []).filter((s) => Number(s.gamesPlayed) === 0).map((s) => s.player),
          ...(goalieStatsItems ?? []).filter((s) => Number(s.gamesPlayed) === 0).map((s) => s.player),
        ]
          .filter(Boolean)
          .map((n) => String(n));

        const unique = Array.from(new Set(zeroGpNames)).sort((a, b) => a.localeCompare(b));
        setEligiblePlayers(unique);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unable to load eligible players.';
        setError(message);
      } finally {
        setLoadingEligible(false);
      }
    };

    loadEligiblePlayers();
  }, [reloadKey]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const usedNames = (rowIndex: number) =>
    new Set(
      selectedPlayers
        .map((name, idx) => (idx === rowIndex ? null : name))
        .filter((n): n is string => !!n)
    );

  const availablePlayersForRow = (rowIndex: number) => {
    const used = usedNames(rowIndex);
    const current = selectedPlayers[rowIndex] || '';
    return eligiblePlayers.filter((name) => !used.has(name) || name === current);
  };

  const addAnotherRow = () => {
    setSelectedPlayers((prev) => {
      if (prev.length >= MAX_ROWS) return prev;
      return [...prev, ''];
    });
  };

  const updateRow = (rowIndex: number, value: string) => {
    setSelectedPlayers((prev) => prev.map((v, i) => (i === rowIndex ? value : v)));
  };

  const selectedNonEmpty = selectedPlayers.filter((p) => !!p);

  const handleRemoveClick = () => {
    setError(null);

    if (selectedNonEmpty.length === 0) {
      setError('Select at least one player to remove.');
      return;
    }

    setPendingDelete(selectedNonEmpty);
    setStep('confirm');
  };

  const handleConfirmRemove = async () => {
    setError(null);

    try {
      if (pendingDelete.length === 0) throw new Error('No players selected.');

      // Clear caches so the next load reflects the deletion
      localStorage.removeItem(CACHE_KEYS.players);
      localStorage.removeItem(CACHE_KEYS.stats);
      localStorage.removeItem(CACHE_KEYS.goalieStats);

      const response = await fetch(deletePlayerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify({
          teamName: 'tigers',
          players: pendingDelete,
        }),
      });

      const data = (await response.json()) as DeletePlayerResponse;

      if (!response.ok || !data.success) {
        throw new Error(data?.details?.errors?.[0] || 'Failed to delete players.');
      }

      setDeleteResult(data);
      setStep('success');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error while deleting players.';
      setDeleteResult(null);
      setError(message);
      setStep('error');
    }
  };

  const resetToSelect = () => {
    setStep('select');
    setPendingDelete([]);
    setDeleteResult(null);
    setSelectedPlayers(['']);
    setError(null);
    setReloadKey((k) => k + 1);
  };

  if (step === 'confirm') {
    return (
      <PageContainer>
        <Title>Confirm Player Deletion</Title>
        <RemoveHeader>
          <HelperText>This will attempt to delete {pendingDelete.length} player(s) from the team.</HelperText>
          <div style={{ marginTop: '0.75rem' }}>
            {pendingDelete.map((name) => (
              <div key={name} style={{ color: 'var(--gray-50)' }}>
                {name}
              </div>
            ))}
          </div>
        </RemoveHeader>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <ActionsRow>
          <SecondaryButton type="button" onClick={() => setStep('select')}>
            Cancel
          </SecondaryButton>
          <SubmitButton type="button" onClick={handleConfirmRemove}>
            Remove player(s)
          </SubmitButton>
        </ActionsRow>

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (step === 'success' && deleteResult) {
    const summary = deleteResult.summary ?? {
      requested: 0,
      deleted: 0,
      skipped: 0,
      notFound: 0,
      errors: 0,
    };

    const details = deleteResult.details ?? {
      deleted: [],
      skipped: [],
      notFound: [],
      errors: [],
    };

    return (
      <PageContainer>
        <Title>Players Removal Complete</Title>
        <Message>
          Requested: {summary.requested}, Deleted: {summary.deleted}, Skipped: {summary.skipped}, Not Found: {summary.notFound}, Errors: {summary.errors}
        </Message>

        <div style={{ marginTop: '1.25rem' }}>
          {details.deleted.length > 0 ? (
            <div>
              <h3 style={{ marginBottom: '0.25rem' }}>Deleted</h3>
              {details.deleted.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          ) : null}

          {details.skipped.length > 0 ? (
            <div style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.25rem' }}>Skipped</h3>
              {details.skipped.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          ) : null}

          {details.notFound.length > 0 ? (
            <div style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.25rem' }}>Not Found</h3>
              {details.notFound.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          ) : null}

          {details.errors.length > 0 ? (
            <div style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.25rem' }}>Errors</h3>
              {details.errors.map((err, idx) => (
                <div key={`${idx}-${err}`}>{err}</div>
              ))}
            </div>
          ) : null}
        </div>

        <ActionsRow>
          <SecondaryButton type="button" onClick={resetToSelect}>
            Remove more players
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (step === 'error') {
    return (
      <PageContainer>
        <Title>Players Not Removed</Title>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetToSelect}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Delete Player(s)</Title>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleRemoveClick();
        }}
      >
        <FieldGroup>
          <Label>
            Eligible Players (GP = 0)<RequiredMark>*</RequiredMark>
          </Label>
          <HelperText>Choose one or more players with no games played this season.</HelperText>
        </FieldGroup>

        <RowsWrap>
          {selectedPlayers.map((value, rowIndex) => (
            <FieldGroup key={`row-${rowIndex}`}>
              <RowLabel>Player {rowIndex + 1}</RowLabel>
              <Select value={value} onChange={(e) => updateRow(rowIndex, e.target.value)}>
                <option value="">Select player</option>
                {availablePlayersForRow(rowIndex).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </FieldGroup>
          ))}
        </RowsWrap>

        {selectedPlayers.length < MAX_ROWS ? (
          <SecondaryButton type="button" onClick={addAnotherRow}>
            + Add another player
          </SecondaryButton>
        ) : null}

        {loadingEligible ? <HelperText>Loading eligible players…</HelperText> : null}
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <SubmitButton type="submit" disabled={loadingEligible}>
          Remove player(s)
        </SubmitButton>

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default DeletePlayer;

